/**
 * OfflineBanner — slim top banner shown when the device loses connectivity.
 *
 * Apple Guideline 4.2 rejects apps that show a blank screen on network loss.
 * The bundled web assets still render (Capacitor serves them locally), so the
 * app never white-screens; this banner makes the offline state explicit while
 * the user keeps navigating cached views.
 *
 * Uses the universal `online`/`offline` window events everywhere, plus the
 * Capacitor Network plugin on native for reliable detection inside the WebView.
 */
import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export function OfflineBanner() {
  const [offline, setOffline] = useState<boolean>(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    let removeNative: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        try {
          const { Network } = await import('@capacitor/network');
          const status = await Network.getStatus();
          setOffline(!status.connected);
          const handle = await Network.addListener('networkStatusChange', (s) =>
            setOffline(!s.connected),
          );
          removeNative = () => {
            void handle.remove();
          };
        } catch {
          /* Network plugin unavailable — window events still cover us */
        }
      })();
    }

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      removeNative?.();
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 bg-gray-900 px-3 py-2 text-xs font-medium text-white app-bar-safe"
    >
      <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
      You&rsquo;re offline — some features are unavailable.
    </div>
  );
}

export default OfflineBanner;
